class KapavyaStickyAtc {
  constructor(element) {
    this.element = element;
    this.formId = element.dataset.productFormId;
    this.form = document.getElementById(this.formId);
    this.productInfo = element.closest('product-info');
    this.variantSelect = element.querySelector('[data-kapavya-sticky-variant]');
    this.quantityInput = element.querySelector('[data-kapavya-sticky-quantity]');
    this.minusButton = element.querySelector('[data-kapavya-sticky-minus]');
    this.plusButton = element.querySelector('[data-kapavya-sticky-plus]');
    this.submitButton = element.querySelector('[data-kapavya-sticky-submit]');
    this.price = element.querySelector('[data-kapavya-sticky-price]');
    this.compare = element.querySelector('[data-kapavya-sticky-compare]');
    this.error = element.querySelector('[data-kapavya-sticky-error]');
    this.confirmedVariantId = '';
    this.pendingSubmitVariantId = '';
    this.syncRequestId = 0;
    this.isSubmitting = false;

    if (!this.form || !this.variantSelect || !this.quantityInput || !this.submitButton) return;

    this.element.dataset.kapavyaStickyInitialized = 'true';
    this.syncFromCurrentVariant();
    this.bindEvents();
    this.bindVisibility();
    this.syncVariantDisplay();
  }

  bindEvents() {
    this.variantSelect.addEventListener('change', () => this.handleStickyVariantChange());
    this.minusButton?.addEventListener('click', () => this.adjustQuantity(-1));
    this.plusButton?.addEventListener('click', () => this.adjustQuantity(1));
    this.submitButton.addEventListener('click', () => this.submit());
    this.form.addEventListener('submit', () => this.enforceSubmittedVariant(), true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!this.element.isConnected) {
          this.variantChangeUnsubscriber?.();
          this.variantChangeUnsubscriber = null;
          return;
        }

        if (data?.sectionId && `product-form-${data.sectionId}` !== this.formId) return;
        this.syncFromMainVariant(data?.variant);
      });
    }

    document.addEventListener('kapavya:cart-error', (event) => {
      this.isSubmitting = false;
      this.setLoading(false);
      this.showError(event.detail?.message || window.cartStrings?.error || 'Unable to add item to cart.');
    });

    document.addEventListener('kapavya:cart-added', () => {
      this.isSubmitting = false;
      this.setLoading(false);
      this.showError('');
    });
  }

  bindVisibility() {
    const mainButton = document.querySelector(`#ProductSubmitButton-${this.formId.replace('product-form-', '')}`);

    if (!mainButton || !('IntersectionObserver' in window)) {
      this.element.hidden = window.scrollY < 500;
      window.addEventListener(
        'scroll',
        () => {
          this.element.hidden = window.scrollY < 500;
        },
        { passive: true }
      );
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.element.hidden = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(mainButton);
  }

  syncFromCurrentVariant() {
    const variantInput = this.form.querySelector('[name="id"]');
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    const variantId = variantInput?.value || urlVariantId;

    if (variantId && this.hasStickyVariant(variantId)) {
      this.variantSelect.value = String(variantId);
      this.confirmedVariantId = String(variantId);
    }
  }

  syncFromMainVariant(variant) {
    const variantId = variant?.id ? String(variant.id) : '';
    if (!variantId || !this.hasStickyVariant(variantId)) return;

    this.confirmedVariantId = variantId;
    this.variantSelect.value = variantId;
    this.syncVariantDisplay();
    this.showError('');
  }

  hasStickyVariant(variantId) {
    return Array.from(this.variantSelect.options).some((option) => option.value === String(variantId));
  }

  handleStickyVariantChange() {
    this.syncVariantDisplay();
    this.showError('');
    this.syncMainVariant(this.variantSelect.value);
  }

  syncVariantDisplay() {
    const option = this.variantSelect.selectedOptions[0];
    if (!option) return;

    const isAvailable = option.dataset.available === 'true';
    this.submitButton.disabled = this.isSubmitting || !isAvailable;
    this.submitButton.textContent = isAvailable
      ? window.variantStrings?.addToCart || 'Add to cart'
      : window.variantStrings?.soldOut || 'Sold out';
    this.price.textContent = option.dataset.price || '';

    if (option.dataset.compare) {
      this.compare.textContent = option.dataset.compare;
      this.compare.hidden = false;
    } else {
      this.compare.hidden = true;
    }
  }

  getVariantOptionValues(variantId) {
    const option = Array.from(this.variantSelect.options).find((item) => item.value === String(variantId));
    if (!option?.dataset.options) return [];

    try {
      const values = JSON.parse(option.dataset.options);
      return Array.isArray(values) ? values.map(String) : [];
    } catch {
      return [];
    }
  }

  getVariantSelectors() {
    return this.productInfo?.querySelector('variant-selects');
  }

  mainSelectorMatchesVariant(variantId) {
    const variantSelectors = this.getVariantSelectors();
    if (!variantSelectors) return this.form.querySelector('[name="id"]')?.value === String(variantId);

    const optionValues = this.getVariantOptionValues(variantId);
    const groups = Array.from(variantSelectors.querySelectorAll('fieldset, .product-form__input--dropdown'));
    if (!optionValues.length || groups.length !== optionValues.length) return false;

    return groups.every((group, index) => {
      const selectedControl = group.querySelector('input:checked') || group.querySelector('select');
      return selectedControl?.value === optionValues[index];
    });
  }

  async syncMainVariant(variantId) {
    const requestedVariantId = String(variantId);
    const requestId = ++this.syncRequestId;
    const variantInput = this.form.querySelector('[name="id"]');

    if (this.confirmedVariantId === requestedVariantId && this.mainSelectorMatchesVariant(requestedVariantId)) {
      return true;
    }

    const variantSelectors = this.getVariantSelectors();
    if (!variantSelectors) {
      if (!variantInput) return false;
      variantInput.value = requestedVariantId;
      variantInput.disabled = false;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.confirmedVariantId = requestedVariantId;
      return true;
    }

    const optionValues = this.getVariantOptionValues(requestedVariantId);
    const groups = Array.from(variantSelectors.querySelectorAll('fieldset, .product-form__input--dropdown'));
    if (!optionValues.length || groups.length !== optionValues.length) return false;

    let eventTarget = null;

    for (let index = 0; index < groups.length; index += 1) {
      const value = optionValues[index];
      const select = groups[index].querySelector('select');

      if (select) {
        const hasValue = Array.from(select.options).some((option) => option.value === value);
        if (!hasValue) return false;
        select.value = value;
        eventTarget = select;
        continue;
      }

      const input = Array.from(groups[index].querySelectorAll('input')).find((item) => item.value === value);
      if (!input) return false;
      input.checked = true;
      eventTarget = input;
    }

    if (!eventTarget) return false;

    const synced = await this.waitForVariantChange(requestedVariantId, () => {
      eventTarget.dispatchEvent(new Event('change', { bubbles: true }));
    });

    return requestId === this.syncRequestId && synced;
  }

  waitForVariantChange(variantId, trigger) {
    if (
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS.variantChange
    ) {
      trigger();
      return Promise.resolve(this.form.querySelector('[name="id"]')?.value === String(variantId));
    }

    return new Promise((resolve) => {
      let timeoutId;
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId && `product-form-${data.sectionId}` !== this.formId) return;
        if (String(data?.variant?.id || '') !== String(variantId)) return;

        clearTimeout(timeoutId);
        unsubscribe();
        this.confirmedVariantId = String(variantId);
        resolve(true);
      });

      timeoutId = window.setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, 6000);

      trigger();
    });
  }

  adjustQuantity(direction) {
    const step = parseInt(this.quantityInput.step, 10) || 1;
    const min = parseInt(this.quantityInput.min, 10) || 1;
    const current = parseInt(this.quantityInput.value, 10) || min;
    this.quantityInput.value = Math.max(min, current + direction * step);
  }

  async submit() {
    if (this.isSubmitting) return;

    const option = this.variantSelect.selectedOptions[0];
    if (!option || option.dataset.available !== 'true') return;

    const variantId = this.variantSelect.value;
    const isSynced = await this.syncMainVariant(variantId);
    if (!isSynced || this.confirmedVariantId !== String(variantId)) {
      this.showError('Please select your size again.');
      this.setLoading(false);
      return;
    }

    this.form = document.getElementById(this.formId);
    const variantInput = this.form?.querySelector('[name="id"]');
    const quantityInput = this.form?.querySelector('[name="quantity"]');

    if (!variantInput) {
      this.showError(window.cartStrings?.error || 'Unable to add item to cart.');
      return;
    }

    this.pendingSubmitVariantId = String(variantId);
    this.enforceSubmittedVariant();

    if (variantInput.value !== this.pendingSubmitVariantId) {
      this.pendingSubmitVariantId = '';
      this.showError('Please select your size again.');
      return;
    }

    if (quantityInput) {
      quantityInput.value = Math.max(
        parseInt(this.quantityInput.min, 10) || 1,
        parseInt(this.quantityInput.value, 10) || 1
      );
    }

    this.showError('');
    this.isSubmitting = true;
    this.setLoading(true);

    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
    } else {
      this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    this.pendingSubmitVariantId = '';
  }

  enforceSubmittedVariant() {
    if (!this.pendingSubmitVariantId) return;

    const variantInput = this.form?.querySelector('[name="id"]');
    if (!variantInput) return;

    variantInput.value = this.pendingSubmitVariantId;
    variantInput.disabled = false;
  }

  setLoading(isLoading) {
    const isAvailable = this.variantSelect.selectedOptions[0]?.dataset.available === 'true';
    this.submitButton.disabled = isLoading || !isAvailable;
    this.submitButton.textContent = isLoading
      ? 'Adding...'
      : isAvailable
        ? window.variantStrings?.addToCart || 'Add to cart'
        : window.variantStrings?.soldOut || 'Sold out';
  }

  showError(message) {
    if (this.error) this.error.textContent = message || '';
  }
}

function initializeKapavyaStickyAtc(root = document) {
  const elements = [];
  if (root.matches?.('[data-kapavya-sticky-atc]')) elements.push(root);
  elements.push(...root.querySelectorAll?.('[data-kapavya-sticky-atc]') || []);

  elements.forEach((element) => {
    if (element.dataset.kapavyaStickyInitialized === 'true') return;
    new KapavyaStickyAtc(element);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeKapavyaStickyAtc());
} else {
  initializeKapavyaStickyAtc();
}

document.addEventListener('product-info:loaded', (event) => initializeKapavyaStickyAtc(event.target));
document.addEventListener('shopify:section:load', (event) => initializeKapavyaStickyAtc(event.target));
